import { DismissableLayer } from "@radix-ui/react-dismissable-layer";
import { toUtcMidnightIso } from "@/components/calendars/allDayDates.ts";
import { rsvpEvent, updateEvent } from "@/components/calendars/api.ts";
import { ParticipantInviteAction } from "@/components/calendars/ParticipantInviteAction.tsx";
import { participantOkthxStatusQueryOptions } from "@/shared/eventParticipantInvites.ts";
import { canEditEventInSidebar, canRsvpOnCalendar } from "@/components/calendars/canEditEvent.ts";
import { useCalendarAccessRole } from "@/components/calendars/eventDetailHooks.ts";
import { combineDateAndTime, isTimeRangeInvalid } from "@/shared/calendars/eventTimeRange.ts";
import {
  ColorIndicator,
  DatePickerCalendar,
  EventColorPicker,
  SectionIcon,
  autoGrowTextareaClass,
} from "@/components/calendars/eventPanelFields.tsx";
import { sanitizeRichTextHtml } from "@/lib/sanitizeHtml.ts";
import { AvatarWithFallback } from "@/components/ui/avatar.tsx";
import {
  SplitPanelHeader,
  panelHeaderControlClass,
  panelHeaderCrumbTitleClass,
  panelTrailingIconClass,
} from "@/components/layout/SplitPanel/SplitPanelHeader.tsx";
import {
  PanelHeaderActionButton,
  PanelHeaderMenu,
  PanelHeaderMenuItem,
  splitPanelHeaderActions,
} from "@/components/layout/SplitPanel/panelHeaderActions.tsx";
import { inlineRenameFieldClass } from "@/components/threads/useInlineRename.ts";
import { Button } from "@/components/ui/button.tsx";
import { ContactInput } from "@/components/ui/contact-input.tsx";
import { Dialog, DialogContent } from "@/components/ui/dialog.tsx";
import { Label } from "@/components/ui/label.tsx";
import { LocationDisplay } from "@/components/ui/location-display.tsx";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover.tsx";
import { Select, SelectContent, SelectItem, SelectTrigger } from "@/components/ui/select.tsx";
import { Separator } from "@/components/ui/separator.tsx";
import { SidebarContent } from "@/components/ui/sidebar.tsx";
import { Textarea } from "@/components/ui/textarea.tsx";
import { Toggle } from "@/components/ui/toggle.tsx";
import { errorToast } from "@/components/ui/toast.tsx";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip.tsx";
import { cn } from "@/lib/utils.ts";
import type { CalendarEvent, CalendarEventParticipant } from "@/types/events.ts";
import { createContactWithHandle } from "@/hooks/useContacts.tsx";
import type { Contact, Handle } from "@/types/contact.ts";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useForm, useStore } from "@tanstack/react-form";
import { createPortal } from "react-dom";
import { Link, useLocation } from "wouter";
import { addDays, differenceInCalendarDays, format, parseISO } from "date-fns";
import {
  ArrowUpRight,
  CalendarCheck,
  CalendarDays,
  Check,
  CircleDot,
  Clock,
  ExternalLink,
  Loader2,
  MapPin,
  Pencil,
  Repeat,
  SquarePen,
  Trash2,
  User,
  Video,
  X,
} from "lucide-react";
import { InlineLocationInput } from "@/components/calendars/InlineLocationInput.tsx";
import { RecurrencePicker } from "@/components/calendars/RecurrencePicker.tsx";
import { rruleToText } from "@/shared/rrule.ts";
import { RecurringEditScopeDialog } from "@/components/calendars/RecurringEditScopeDialog.tsx";
import { EventBriefSection } from "@/components/calendars/EventBriefSection.tsx";
import { EventOkthxActionLine } from "@/components/calendars/EventOkthxActionLine.tsx";
import { RemindersSection } from "@/components/calendars/RemindersSection.tsx";
import {
  endTimeOptions,
  formatTimeLabelCompact,
  minutesToTimeString,
  timeOptionsWithValue,
  timeStringToMinutes,
} from "@/components/calendars/timeOptions.ts";
import type { Availability, ReminderOverride } from "@/types/artifacts.ts";
import type { RecurringEditScope } from "@/types/events.ts";
import type { MutableRefObject, ReactNode, RefObject } from "react";
import { useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";

/** Imperative close-guard published by EventDetailPanel via `closeGuardRef`.
 *  Call with the action to run once the user resolves any unsaved changes. */
export type EventDetailCloseGuard = (proceed: () => void) => void;

/**
 * The panel's title-bar actions, published to a parent that renders its own
 * header (the canvas card). Rename stays owned by the panel, since it drives
 * the panel's own form state, so the parent gets a trigger rather than
 * reimplementing it. Delete is the host's — see `onRequestDelete`.
 */
export type EventDetailActions = {
  /**
   * The title as the panel currently shows it, unsaved rename included, for a
   * host drawing its own chrome around the portalled title.
   */
  title: string;
  /** The event is editable, so it can be renamed. */
  canRename: boolean;
  /** The event exists on a provider calendar, so it can be deleted. */
  canDelete: boolean;
  /** Switch the title to its inline editor. */
  startRename: () => void;
  /** Ask the host to start its delete flow. No-op if it supplied none. */
  requestDelete: () => void;
};

// --- Helpers ---

const MEETING_URL_PATTERN =
  /https:\/\/(meet\.google\.com\/[a-z0-9-]+|teams\.microsoft\.com\/[^\s<)"']+)/i;

// Generic conference labels Google/Microsoft set on `location` when no
// physical location was provided. Treated as "no physical location" so users
// can type their own address without thinking they'll lose the meet link.
const CONFERENCE_LABEL_PATTERN =
  /^(google meet|microsoft teams(?: meeting)?|teams(?: meeting)?|zoom)$/i;

/**
 * Returns the user-facing physical location. When a virtual meeting is
 * attached, strips conference-only metadata (the meet URL itself, or a generic
 * label like "Google Meet") so the location input is free for a real address.
 */
function getPhysicalLocation(rawLocation: string | undefined, hasVirtualMeeting: boolean): string {
  const value = rawLocation ?? "";
  if (!hasVirtualMeeting) return value;
  const trimmed = value.trim();
  if (!trimmed) return "";
  if (CONFERENCE_LABEL_PATTERN.test(trimmed)) return "";
  if (MEETING_URL_PATTERN.test(trimmed) && trimmed.replace(MEETING_URL_PATTERN, "").trim() === "") {
    return "";
  }
  return value;
}

function isoToTimeString(iso: string): string {
  return format(parseISO(iso), "HH:mm");
}

function stateToRsvp(state?: string): "accepted" | "tentative" | "declined" | undefined {
  switch (state) {
    case "attending":
      return "accepted";
    case "tentative":
      return "tentative";
    case "declined":
      return "declined";
    default:
      return undefined;
  }
}

function getMeetLinkLabel(url: string): string {
  if (url.includes("meet.google.com")) return "Join Google Meet";
  if (url.includes("zoom.us")) return "Join Zoom";
  if (url.includes("teams.microsoft")) return "Join Teams";
  return "Join meeting";
}

// RSVP status → badge color. Green accepted, yellow tentative, red declined; any
// other state (no response) shows no badge.
const PARTICIPANT_STATUS_COLOR: Record<string, string> = {
  attending: "bg-green-500",
  tentative: "bg-yellow-400",
  declined: "bg-red-500",
};

/** Descriptive RSVP status phrase for a participant's avatar tooltip. */
function participantStatusTooltip(name: string, state?: string): string {
  switch (state) {
    case "attending":
      return `${name} accepted`;
    case "declined":
      return `${name} declined`;
    case "tentative":
      return `${name} is considering`;
    default:
      return `${name} has not responded`;
  }
}

/**
 * RSVP status badge straddling the top-right edge of a participant avatar.
 * Accepted shows a check, declined an X, tentative a "?". `ring-2 ring-background`
 * separates it from the avatar.
 */
const ParticipantStatusBadge = ({ state }: { state?: string }) => {
  const color = state ? PARTICIPANT_STATUS_COLOR[state] : undefined;
  if (!color) return null;
  return (
    <span
      className={cn(
        "absolute -top-0.5 -right-0.5 inline-flex size-2.5 items-center justify-center rounded-full ring-1 ring-background",
        color,
      )}
    >
      {state === "attending" && <Check className="size-2 text-white" strokeWidth={3} />}
      {state === "declined" && <X className="size-2 text-white" strokeWidth={3} />}
      {state === "tentative" && (
        <span className="text-[7px] font-bold leading-none text-white">?</span>
      )}
    </span>
  );
};

const RSVP_OPTIONS = [
  { value: "accepted", label: "Yes" },
  { value: "declined", label: "No" },
  { value: "tentative", label: "Maybe" },
] as const;

// --- Inline editable panel ---

// Pill SelectTrigger override for the RecurrencePicker, matching the bordered
// Reminders pills. RecurrencePicker's default trigger is borderless inline text;
// these classes win (twMerge, last-class-per-property) to restore the border,
// padding, fixed height, and foreground text. The keyboard-only focus ring comes
// from SelectTrigger's base `focus-visible:` styles — we deliberately don't add a
// `focus:` ring here, which would reappear on mouse click.
const pillSelectTriggerClass = "border h-7 px-2 text-foreground";

/**
 * Render a description's HTML safely (sanitized, tight allowlist) with links
 * opening in a new tab. Used in the read state of the description field — the
 * raw HTML source is only shown while editing.
 */
function DescriptionHtml({ html, className }: { html: string; className?: string }) {
  const sanitized = useMemo(() => sanitizeRichTextHtml(html, { openLinksInNewTab: true }), [html]);
  return (
    <div
      className={cn(
        "text-sm break-words [&_a]:text-primary [&_a]:underline [&_p]:mb-1 [&_p:last-child]:mb-0 [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5",
        className,
      )}
      // biome-ignore lint/security/noDangerouslySetInnerHtml: sanitized with DOMPurify (tight allowlist)
      dangerouslySetInnerHTML={{ __html: sanitized }}
    />
  );
}

/**
 * The event's title at rest.
 *
 * One treatment, because there is only one kind of bar it can land in: the
 * panel's own row is a `SplitPanelHeader` too, so whether the title renders here
 * or portals into a parent's header, it is in a panel header either way — one
 * height, held across every panel on screen.
 *
 * That height is why it truncates rather than wraps. The tail of an event title
 * is usually its distinguishing part ("... with Legal" / "... with Finance"), so
 * the tooltip carries it. Radix opens that on hover and on focus, and an
 * editable title is focusable, so the full name is reachable without a pointer.
 * Failing that, renaming shows it, since that editor is allowed to grow the row.
 */
const EventTitleText = ({ children, onEdit }: { children: ReactNode; onEdit?: () => void }) => {
  return (
    // The row's share of the header belongs to this wrapper, not to the words,
    // which is where thread detail puts it too. On the span itself it would
    // stretch the hover wash and the click target across the whole strip, so a
    // click an inch to the right of a short title would open rename.
    <div className="flex min-w-0 flex-1 items-center">
      <Tooltip>
        <TooltipTrigger asChild>
          {onEdit ? (
            // Keyboard access is tabIndex + onKeyDown, the same shape the description
            // field further down uses, and it reads to assistive tech exactly as a
            // button would.
            // biome-ignore lint/a11y/useSemanticElements: `Button`'s own base is inline-flex, centred and nowrap, which a title cannot be: it has to stay a block that truncates on the crumb class's terms. Neutralising that costs more overrides than the element saves.
            <span
              role="button"
              tabIndex={0}
              onClick={onEdit}
              onKeyDown={(e) => {
                if (e.key !== "Enter") return;
                e.preventDefault();
                onEdit();
              }}
              className={cn(
                panelHeaderCrumbTitleClass,
                // The same affordance thread detail gives its title: the cursor says
                // text, and a wash on hover says the words themselves are the control.
                "cursor-text rounded-sm transition-colors hover:bg-muted/50",
              )}
            >
              {children}
            </span>
          ) : (
            // Nothing to edit, so nothing to offer: a read-only event's title with a
            // caret and a tab stop would promise an editor that never opens.
            <span className={panelHeaderCrumbTitleClass}>{children}</span>
          )}
        </TooltipTrigger>
        {/* The same treatment thread detail gives its own: bottom, so it clears
            the header row rather than covering the controls beside the title. */}
        <TooltipContent className="px-2.5 py-1 rounded-sm w-fit max-w-[90vw]" side="bottom">
          {onEdit ? <>Click to edit · {children}</> : children}
        </TooltipContent>
      </Tooltip>
    </div>
  );
};

/**
 * The title's inline rename editor. Auto-grows and soft-wraps, so Enter commits
 * rather than inserting a newline — a title has no use for hard breaks. Escape
 * reverts, the other half of the contract every inline rename here keeps.
 *
 * It takes that key by joining Radix's layer stack, so whatever the user opened
 * over the rename answers first and this field only hears the Escape aimed at
 * it. `stopPropagation` but never `preventDefault`: the former keeps the key off
 * the screen-level back shortcut, which would navigate out from under a panel
 * the user meant only to stop renaming in; the latter is Radix's to call, on the
 * one path where it is right. Outside interaction stays inert here because the
 * panel commits on an outside pointer-down of its own.
 *
 * Committing only leaves the editor; the new value stays in the form until the
 * panel is saved, like every other field.
 */
const EventTitleEditor = ({
  value,
  onChange,
  onCommit,
  onCancel,
  textareaRef,
}: {
  value: string;
  onChange: (next: string) => void;
  onCommit: () => void;
  onCancel: () => void;
  textareaRef: RefObject<HTMLTextAreaElement | null>;
}) => {
  return (
    <DismissableLayer
      asChild
      onEscapeKeyDown={(e) => e.stopPropagation()}
      onInteractOutside={(e) => e.preventDefault()}
      onDismiss={onCancel}
    >
      <Textarea
        ref={textareaRef}
        rows={1}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={(e) => {
          if (e.key !== "Enter") return;
          e.preventDefault();
          onCommit();
        }}
        className={cn(
          // The shared look for a title being edited, the one thread detail's
          // rename wears: no border, no fill, no ring, and the same box the
          // resting title occupied. What marks the field as live is the selection
          // sitting on the text, and chrome of any kind competes with that.
          inlineRenameFieldClass,
          // Size is named here rather than inherited: the resting title sets its
          // own through the crumb class, so there is nothing for the field to
          // inherit. Both halves are load-bearing — the `md:` one displaces the
          // constant's own `md:text-[length:inherit]`, which tailwind-merge keeps
          // otherwise, since an unmodified class cannot displace a modified one.
          "flex-1 resize-none text-base font-medium md:text-base",
          autoGrowTextareaClass,
        )}
      />
    </DismissableLayer>
  );
};

export const EventDetailPanel = ({
  event,
  prospectiveStart,
  prospectiveEnd,
  onClose,
  onCancel,
  hideClose = false,
  onExpand,
  titleContainer,
  closeGuardRef,
  onActionsChange,
  onDirtyChange,
  onNavigateAway,
  onRequestDelete,
}: {
  event: CalendarEvent;
  /** Drag-to-reschedule "Continue editing" path: seed start/end from these
   *  ISO strings instead of the event's stored times. Applied only at mount;
   *  a later cache update to `event.start.dateTime` cannot clobber the user's
   *  dragged-to choice because `useState` initializers run once. */
  prospectiveStart?: string;
  prospectiveEnd?: string;
  onClose: () => void;
  onCancel: () => void;
  /** Hide the X close button (e.g. when a parent already provides back navigation). */
  hideClose?: boolean;
  /**
   * Promote this panel to the fuller surface for the same event, from its own
   * title bar. Omitted where the panel already *is* that surface — a canvas card
   * has nowhere further to go — and ignored under {@link titleContainer}, whose
   * host owns the row this would sit in.
   *
   * Called raw, unlike the close beside it: a host with unsaved edits to protect
   * routes this through the guard it already holds via {@link closeGuardRef}.
   * Guarding here too would loop — discarding runs the pending action without
   * clearing the form, so the second guard would find it dirty again.
   */
  onExpand?: () => void;
  /**
   * Render the title — static text, or the inline rename editor once it opens —
   * into this element instead of the panel's own title bar, which is then
   * dropped entirely. Lets a parent that already has a header (the canvas card)
   * own the title without a second bar appearing under it, while the field stays
   * bound to this panel's form. Pair with {@link onActionsChange} so rename and
   * delete stay reachable.
   */
  titleContainer?: HTMLElement | null;
  /** When provided, the panel publishes an imperative `attemptClose(proceed)`
   *  here. Parents (the popover, the canvas card, the event page) route their
   *  dismiss paths through it
   *  so unsaved edits prompt a Save/Discard confirmation first. */
  closeGuardRef?: MutableRefObject<EventDetailCloseGuard | null>;
  /** Publishes {@link EventDetailActions} for a parent-rendered title bar, and
   *  `null` on unmount. Must be stable, since it fires from an effect. */
  onActionsChange?: (actions: EventDetailActions | null) => void;
  /** Notified whenever the unsaved-changes state flips — lets a parent make the
   *  popover modal while dirty so an outside click can't act on the calendar. */
  onDirtyChange?: (dirty: boolean) => void;
  /**
   * Close this surface when a link inside the panel navigates away from it.
   * Overlays (the click popover, the canvas card) pass their dismiss so they
   * don't linger in front of what the user just opened.
   *
   * Distinct from `onClose`, and deliberately not defaulted to it: the
   * standalone `EventDetail` page closes by navigating *back*, which would fight
   * the navigation the link is making. That surface omits this, and the link
   * still routes through the unsaved-changes prompt on its way out.
   */
  onNavigateAway?: () => void;
  /**
   * Start the delete flow, which the host owns (see `useEventDeleteFlow`).
   *
   * Not handled here, and that is deliberate (BIG-8266). A confirmation rendered
   * by this panel is a child of the surface it is asking about, so the hosts'
   * key-based remount — on `gcalId`, a cancelled edit, an external reschedule —
   * tore it down mid-decision. The host closes this panel and keeps the
   * confirmation, which is the only way round that.
   *
   * Omitted where nothing hosts a delete flow; `canDelete` still reports
   * whether the event *could* be deleted, so a host that wants the affordance
   * supplies this and one that does not simply never shows it.
   */
  onRequestDelete?: () => void;
}) => {
  const queryClient = useQueryClient();
  const originalParticipants = event.participants ?? [];
  const selfParticipant = event.participants?.find((p) => p.self);
  const isOrganizer = selfParticipant?.role === "organizer";
  const canEdit = canEditEventInSidebar(event);
  const currentRsvp = stateToRsvp(selfParticipant?.state);
  const isAttendee = !!selfParticipant;
  // A read-only copy of an event takes the tap, then Google refuses the write
  // and the card is left claiming a reply that was never sent (BIG-7628).
  const calendarAccessRole = useCalendarAccessRole(event.calendarId);
  const showRsvp =
    !isOrganizer &&
    isAttendee &&
    !!event.externalEventId &&
    event.status !== "cancelled" &&
    canRsvpOnCalendar(calendarAccessRole);
  // Reminders are personal — every attendee edits their own copy on their own
  // calendar, regardless of who organized the event or guestsCanModify. Participants
  // can set reminders on an event they otherwise can't modify.
  const canEditReminders = !!event.externalEventId && event.status !== "cancelled";

  const isPendingMeeting = event.meetingUrl === "pending";
  const meetLink =
    (!isPendingMeeting && event.meetingUrl) || event.location?.match(MEETING_URL_PATTERN)?.[0];
  // Captured once at mount (matches the pattern for `originalEndDate` /
  // `originalRecurrenceJson`) so a background cache update to `event.location`
  // or `event.meetingUrl` mid-edit can't shift the baseline used by the dirty
  // check — which would silently flip a clean field dirty or vice versa.
  const [initialPhysicalLocation] = useState(() =>
    getPhysicalLocation(event.location, !!event.meetingUrl),
  );
  // Same freeze, for the same reason: the form seeds from this and the dirty
  // check compares against it, so both stay pinned to what was on screen when
  // the panel opened rather than to whatever the cache holds now.
  const [originalSummary] = useState(event.summary);

  // State — seed from prospective drag times if provided (drag-to-reschedule
  // "Continue editing" path); else from the event's saved times.
  const initialStartIso = prospectiveStart ?? event.start.dateTime;
  const initialEndIso = prospectiveEnd ?? event.end.dateTime;
  // All-day events arrive with an *exclusive* end date (the midnight after the
  // last day). The UI works with an *inclusive* end day, so subtract one day
  // when seeding. This applies to a prospective end too: FullCalendar reports an
  // all-day drag's end exclusively, exactly as the stored event does, so
  // exempting that path grew the event by a day on every save.
  const seededAllDay = !!event.isAllDay;
  const [isAllDay, setIsAllDay] = useState(seededAllDay);
  const seededAvailability: Availability = event.availability ?? "busy";
  const [availability, setAvailability] = useState<Availability>(seededAvailability);
  const [startDate, setStartDate] = useState<Date>(() => parseISO(initialStartIso));
  const [endDate, setEndDate] = useState<Date>(() =>
    seededAllDay ? addDays(parseISO(initialEndIso), -1) : parseISO(initialEndIso),
  );
  const [startTime, setStartTime] = useState(() => isoToTimeString(initialStartIso));
  const [endTime, setEndTime] = useState(() => isoToTimeString(initialEndIso));
  const startTimeOptions = useMemo(() => timeOptionsWithValue(startTime), [startTime]);
  // The end time is "next day" when its date is past the start date — used both
  // to label cross-midnight options and to keep the duration math correct.
  const endIsNextDay = useMemo(
    () => format(endDate, "yyyy-MM-dd") > format(startDate, "yyyy-MM-dd"),
    [endDate, startDate],
  );
  const endTimeOpts = useMemo(
    () => endTimeOptions(startTime, endTime, endIsNextDay),
    [startTime, endTime, endIsNextDay],
  );
  const [hasMeeting, setHasMeeting] = useState(!!event.meetingUrl);
  // Frozen at mount like initialPhysicalLocation / originalEndDate (BIG-4107).
  // Derived per-render this would track event.meetingUrl, so a background
  // refresh flipping it (e.g. undefined → "pending" while the add-meeting
  // request lands) moves the baseline mid-edit and silently clears
  // meetingChanged — taking the unsaved-changes guard with it.
  const [originalHasMeeting] = useState(() => !!event.meetingUrl);
  const [participants, setParticipants] =
    useState<CalendarEventParticipant[]>(originalParticipants);
  const [addedHandleIds, setAddedHandleIds] = useState<string[]>([]);
  const [removedHandleIds, setRemovedHandleIds] = useState<string[]>([]);
  const [description, setDescription] = useState(event.description ?? "");
  const [descriptionDirty, setDescriptionDirty] = useState(false);
  // `descriptionOpen` = editing the raw HTML source. Defaults to closed: a
  // saved description renders as HTML (read state), an empty one shows the
  // "Add description" affordance; clicking either enters edit mode.
  const [descriptionOpen, setDescriptionOpen] = useState(false);
  // Ref drives explicit focus-on-click only; auto-grow is handled by CSS
  // field-sizing (see autoGrowTextareaClass), so no sizing effect is needed.
  const descriptionTextareaRef = useRef<HTMLTextAreaElement>(null);
  // Enter edit mode and focus the textarea once it mounts (rAF waits for the
  // state flip to render).
  const enterDescriptionEdit = () => {
    setDescriptionOpen(true);
    requestAnimationFrame(() => descriptionTextareaRef.current?.focus());
  };
  const [recurrence, setRecurrence] = useState<string[] | undefined>(event.recurrence);
  // A draft over the server value, not a mirror of it: undefined means "no
  // pending change", so a backfill or socket refetch flows straight through and
  // can never be mistaken for a local edit.
  const [remindersDraft, setRemindersDraft] = useState<ReminderOverride[] | undefined>(undefined);
  const reminders = remindersDraft ?? event.reminders;
  // Dropping the draft on a match makes a revert a real revert, so it can't
  // outlive a later server change and send the old array back.
  const handleRemindersChange = (next: ReminderOverride[]) =>
    setRemindersDraft(JSON.stringify(next) === JSON.stringify(event.reminders) ? undefined : next);
  const [colorId, setColorId] = useState<string | undefined>(event.colorId);
  const [originalColorId] = useState(event.colorId);
  const [showEditScopeDialog, setShowEditScopeDialog] = useState(false);
  const pendingEditScopeRef = useRef<RecurringEditScope | undefined>(undefined);

  const originalStartTime = useMemo(
    () => isoToTimeString(event.start.dateTime),
    [event.start.dateTime],
  );
  const originalEndTime = useMemo(() => isoToTimeString(event.end.dateTime), [event.end.dateTime]);
  const originalStartDate = useMemo(() => parseISO(event.start.dateTime), [event.start.dateTime]);
  // Captured once at mount (via useState initializer) so a background cache
  // update to event.recurrence / event.end.dateTime — e.g. after a sync
  // round-trip that normalizes the ISO string — can't re-derive the
  // "original" value mid-edit and silently flip the dirty check back to
  // false. Paired with `originalEndDate` below (BIG-4107).
  const [originalRecurrenceJson] = useState(() => JSON.stringify(event.recurrence));
  // Inclusive end day for all-day events (the stored end is exclusive), mirroring
  // the `endDate` seed above so the dirty check compares like with like.
  const [originalEndDate] = useState(() =>
    seededAllDay ? addDays(parseISO(event.end.dateTime), -1) : parseISO(event.end.dateTime),
  );

  const handleStartTimeChange = (newStartTime: string) => {
    // Preserve the event's duration, measured in absolute minutes (so an end
    // that already crosses midnight stays the same length), then re-derive the
    // end time + date. Floored at 15 min so the end never lands on/before start.
    const prevEndAbs = timeStringToMinutes(endTime) + (endIsNextDay ? 24 * 60 : 0);
    const duration = Math.max(15, prevEndAbs - timeStringToMinutes(startTime));
    const newEndAbs = timeStringToMinutes(newStartTime) + duration;
    setStartTime(newStartTime);
    setEndTime(minutesToTimeString(newEndAbs));
    setEndDate(newEndAbs >= 24 * 60 ? addDays(startDate, 1) : startDate);
  };

  const handleEndTimeChange = (newEndTime: string) => {
    // The end list only offers times after start (incl. flagged next-day slots),
    // so we just adopt the choice and set its date from the option's nextDay flag.
    const option = endTimeOpts.find((o) => o.value === newEndTime);
    setEndTime(newEndTime);
    setEndDate(option?.nextDay ? addDays(startDate, 1) : startDate);
  };

  const handleStartDateChange = (newDate: Date) => {
    // Shift endDate by the same number of calendar days. The picker hands back
    // midnight-anchored dates while startDate carries the event's time of day,
    // so a raw ms diff rounds to zero for an evening event moved one day
    // (BIG-7535: 8 PM → next day is a 4-hour diff, not 24) and the end stayed
    // behind, producing the backwards range the server now rejects.
    const dayDiff = differenceInCalendarDays(newDate, startDate);
    setStartDate(newDate);
    setEndDate(addDays(endDate, dayDiff));
  };

  /** All-day end-date picker. The end day is inclusive and may not precede the
   *  start day; clamp it up to the start day when the user picks earlier. */
  const handleEndDateChange = (newDate: Date) => {
    const clamped =
      format(newDate, "yyyy-MM-dd") < format(startDate, "yyyy-MM-dd") ? startDate : newDate;
    setEndDate(clamped);
  };

  /** Toggle all-day. When switching to a timed event from an all-day one whose
   *  times are still midnight, seed a sensible default window (9–10 AM) so the
   *  user isn't left with a 12:00 AM – 12:00 AM event. */
  const handleAllDayChange = (next: boolean) => {
    setIsAllDay(next);
    if (!next && startTime === "00:00" && endTime === "00:00") {
      setStartTime("09:00");
      setEndTime("10:00");
    }
  };

  const timeRangeInvalid = isTimeRangeInvalid({ isAllDay, startDate, startTime, endDate, endTime });

  // Dirty tracking
  const form = useForm({
    defaultValues: {
      summary: originalSummary,
      location: initialPhysicalLocation,
    },
    onSubmit: async ({ value }) => {
      if (!event.externalEventId || !event.gcalId) return;
      // The footer Save button is disabled on an invalid range, but the
      // unsaved-changes dialog and the recurring-scope confirm both call
      // form.handleSubmit() directly — guard the submit itself so no path
      // sends a backwards range to the server (BIG-7535).
      if (timeRangeInvalid) return;

      // Determine edit scope for recurring events:
      // - Recurrence change → always applies to all events (no dialog)
      // - Other field changes → ask user which scope (this/following/all)
      let editScope = pendingEditScopeRef.current;
      if (event.isRecurring && recurrenceDirty) {
        editScope = "allEvents";
      } else if (event.isRecurring && !editScope) {
        setShowEditScopeDialog(true);
        return;
      }

      const dateChanged =
        format(startDate, "yyyy-MM-dd") !== format(originalStartDate, "yyyy-MM-dd") ||
        format(endDate, "yyyy-MM-dd") !== format(originalEndDate, "yyyy-MM-dd");
      const timeChanged =
        dateChanged || startTime !== originalStartTime || endTime !== originalEndTime;
      const allDayChanged = isAllDay !== seededAllDay;
      const datesChanged = timeChanged || allDayChanged;
      const summaryChanged = value.summary !== originalSummary;
      const locationChanged = value.location !== initialPhysicalLocation;
      const meetingChanged = hasMeeting !== originalHasMeeting;

      // For all-day, send UTC-midnight dates with an *exclusive* end (the
      // midnight after the inclusive last day). For timed events, send the
      // combined local date+time. `isAllDay` is sent whenever it's relevant —
      // i.e. the event is all-day, or the toggle just flipped — so a plain
      // timed reschedule keeps the historical timed-only payload.
      const startDateTime = datesChanged
        ? isAllDay
          ? toUtcMidnightIso(startDate)
          : combineDateAndTime(startDate, startTime)
        : undefined;
      const endDateTime = datesChanged
        ? isAllDay
          ? toUtcMidnightIso(addDays(endDate, 1))
          : combineDateAndTime(endDate, endTime)
        : undefined;

      await updateMutation.mutateAsync({
        sourceEntId: event.gcalId,
        calendarId: event.calendarId,
        externalEventId: event.externalEventId,
        summary: summaryChanged ? value.summary : undefined,
        description: descriptionDirty ? description : undefined,
        location: locationChanged ? value.location : undefined,
        startDateTime,
        endDateTime,
        isAllDay: datesChanged && (isAllDay || allDayChanged) ? isAllDay : undefined,
        availability: availabilityChanged ? availability : undefined,
        attendeesToAdd: addedHandleIds.length ? addedHandleIds : undefined,
        attendeesToRemove: removedHandleIds.length ? removedHandleIds : undefined,
        addVirtualMeeting: meetingChanged ? hasMeeting : undefined,
        recurrence: recurrenceDirty ? (recurrence ?? null) : undefined,
        reminders: remindersDirty ? (reminders ?? []) : undefined,
        // Empty string clears the override back to the calendar default.
        colorId: colorDirty ? (colorId ?? "") : undefined,
        editScope,
      });
      pendingEditScopeRef.current = undefined;
    },
  });

  // The title as it currently reads, draft included. The visible title renders
  // from the form field, so anything else naming this event — the actions menu
  // it opens onto, the card header above it — has to read the same value or it
  // labels itself with a name the user has already replaced.
  const draftSummary = useStore(form.store, (s) => s.values.summary);

  const [summaryDirty, setSummaryDirty] = useState(false);
  // Title is static until the user clicks it or picks "Rename" from the kebab;
  // then it becomes an inline auto-growing textarea.
  const [isRenaming, setIsRenaming] = useState(false);
  const renameTextareaRef = useRef<HTMLTextAreaElement>(null);
  // The title as rename opened on it, so cancelling has something to restore.
  const renameOpenValueRef = useRef("");
  useEffect(() => {
    if (!isRenaming) return;
    // Focus a frame later so the dropdown's close-focus dance finishes first.
    const focusId = requestAnimationFrame(() => {
      const node = renameTextareaRef.current;
      if (!node) return;
      node.focus();
      // Select rather than place a caret, as every other inline rename here
      // does: typing then replaces the whole name, which is what renaming an
      // event almost always means. A caret at the end asks the user to clear it
      // first.
      node.select();
    });
    // Commit on an outside pointer-down (covers mouse + touch). We deliberately
    // do NOT commit on the textarea's onBlur: blur fires spuriously while the
    // dropdown restores focus on close, which would exit rename immediately.
    const onPointerDown = (e: PointerEvent) => {
      const node = renameTextareaRef.current;
      if (node && !node.contains(e.target as Node)) setIsRenaming(false);
    };
    document.addEventListener("pointerdown", onPointerDown);
    return () => {
      cancelAnimationFrame(focusId);
      document.removeEventListener("pointerdown", onPointerDown);
    };
  }, [isRenaming]);

  /**
   * Escape reverts, the contract every inline rename here keeps.
   *
   * The editor answers for the key by joining Radix's layer stack rather than
   * by listening itself, because "is something above me" is a question only
   * that stack can answer: a listener of our own is ordered against Radix by
   * registration alone, so it would also swallow the Escape meant for a delete
   * or recurring-scope dialog opened while a rename is still live.
   */
  const cancelRename = useCallback(() => {
    const restored = renameOpenValueRef.current;
    form.setFieldValue("summary", restored);
    setSummaryDirty(restored !== originalSummary);
    setIsRenaming(false);
  }, [form, originalSummary]);
  const [locationDirty, setLocationDirty] = useState(false);
  // The row starts collapsed either way: a saved location shows as a maps link
  // with a pencil, an empty one as a small "Add a location" affordance that
  // matches "Add video call". The input only exists while actively editing.
  const [locationOpen, setLocationOpen] = useState(false);

  const startDateChanged =
    format(startDate, "yyyy-MM-dd") !== format(originalStartDate, "yyyy-MM-dd");
  const endDateChanged = format(endDate, "yyyy-MM-dd") !== format(originalEndDate, "yyyy-MM-dd");
  const timeChanged =
    startDateChanged ||
    endDateChanged ||
    startTime !== originalStartTime ||
    endTime !== originalEndTime;
  const meetingChanged = hasMeeting !== originalHasMeeting;
  const participantsDirty = addedHandleIds.length > 0 || removedHandleIds.length > 0;

  const recurrenceDirty = JSON.stringify(recurrence) !== originalRecurrenceJson;
  // Only treat reminders as dirty once the server-side sync has confirmed
  // what the provider has. Otherwise an un-backfilled event would look
  // "dirty" to the form (setting undefined from a never-extracted source)
  // and a Save would clobber whatever the provider actually has.
  const remindersLoading = event.remindersSynced === false;
  // Still compared to the server so the panel goes clean on its own when the
  // server converges on the draft (the same edit saved elsewhere).
  const remindersDirty =
    !remindersLoading &&
    remindersDraft !== undefined &&
    JSON.stringify(remindersDraft) !== JSON.stringify(event.reminders);
  const colorDirty = colorId !== originalColorId;
  const isAllDayChanged = isAllDay !== seededAllDay;
  const availabilityChanged = availability !== seededAvailability;
  const isDirty =
    summaryDirty ||
    locationDirty ||
    timeChanged ||
    isAllDayChanged ||
    availabilityChanged ||
    meetingChanged ||
    participantsDirty ||
    descriptionDirty ||
    recurrenceDirty ||
    remindersDirty ||
    colorDirty;

  // --- Unsaved-changes close guard ---------------------------------------
  // A dirty panel intercepts dismiss (X / outside-click / Escape / the card's
  // scrim or Back) and asks the user to Save or Discard first, so edits aren't lost.
  const hasUnsavedChanges = isDirty && (canEdit || (remindersDirty && canEditReminders));
  // The action to run once the user resolves the unsaved-changes prompt.
  const [pendingClose, setPendingClose] = useState<(() => void) | null>(null);
  // Set when the user chose "Save" — run after the save succeeds.
  const closeAfterSaveRef = useRef<(() => void) | null>(null);

  const attemptClose = useCallback(
    (proceed: () => void) => {
      if (hasUnsavedChanges) setPendingClose(() => proceed);
      else proceed();
    },
    [hasUnsavedChanges],
  );
  useImperativeHandle(closeGuardRef, () => attemptClose, [attemptClose]);
  useEffect(() => {
    onDirtyChange?.(hasUnsavedChanges);
  }, [hasUnsavedChanges, onDirtyChange]);

  // Mutations
  const updateMutation = useMutation({
    mutationFn: updateEvent,
    onMutate: async (variables) => {
      // Optimistically mark sibling instances as pending sync for scope edits
      const recurringId = event.recurringEventId;
      const editScope = variables.editScope;
      if (!recurringId || (editScope !== "allEvents" && editScope !== "thisAndFollowing")) return;

      await queryClient.cancelQueries({ queryKey: ["calendarEvents"] });
      queryClient.setQueriesData<{ events: CalendarEvent[] }>(
        { queryKey: ["calendarEvents"] },
        (old) => {
          if (!old) return old;
          return {
            ...old,
            events: old.events.map((e) => {
              if (e.gcalId === event.gcalId || e.recurringEventId !== recurringId) return e;
              if (editScope === "allEvents") return { ...e, pendingSyncUpdate: true };
              if (editScope === "thisAndFollowing" && e.start.dateTime >= event.start.dateTime) {
                return { ...e, pendingSyncUpdate: true };
              }
              return e;
            }),
          };
        },
      );
    },
    onSuccess: (result) => {
      if (result.event) {
        const updated = result.event;
        // Update caches with the server response; socket will trigger refetch for siblings
        queryClient.setQueryData(["event", event.gcalId], updated);
        queryClient.setQueriesData<{ events: CalendarEvent[] }>(
          { queryKey: ["calendarEvents"] },
          (old) => {
            if (!old) return old;
            return {
              ...old,
              events: old.events.map((e) => (e.gcalId === event.gcalId ? updated : e)),
            };
          },
        );
      }
      // A source's instanceKey embeds its start time, so rescheduling changes it.
      // Without this the spotlight keeps matching the pre-edit key.
      queryClient.invalidateQueries({ queryKey: ["source"] });
      // An added attendee needs a status before their Invite button can show.
      queryClient.invalidateQueries({
        queryKey: participantOkthxStatusQueryOptions(event.gcalId).queryKey,
      });
      onCancel();
      // If the save was triggered from the unsaved-changes prompt, run the
      // deferred close now that the edit landed.
      const after = closeAfterSaveRef.current;
      closeAfterSaveRef.current = null;
      if (after) after();
    },
    onError: () => {
      closeAfterSaveRef.current = null;
      errorToast("Failed to update event");
      // Roll back optimistic update by refetching
      queryClient.invalidateQueries({ queryKey: ["calendarEvents"] });
      queryClient.invalidateQueries({ queryKey: ["source"] });
    },
  });

  const rsvpMutation = useMutation({
    mutationFn: rsvpEvent,
    onSuccess: (result) => {
      if (result.event) {
        const updated = result.event;
        queryClient.setQueryData(["event", event.gcalId], updated);
        queryClient.setQueriesData<{ events: CalendarEvent[] }>(
          { queryKey: ["calendarEvents"] },
          (old) => {
            if (!old) return old;
            return {
              ...old,
              events: old.events.map((e) => (e.gcalId === event.gcalId ? updated : e)),
            };
          },
        );
      }
      queryClient.invalidateQueries({ queryKey: ["calendarEvents"] });
      queryClient.invalidateQueries({ queryKey: ["event", event.gcalId] });
      // A source's instanceKey embeds its start time, so rescheduling changes it.
      // Without this the spotlight keeps matching the pre-edit key.
      queryClient.invalidateQueries({ queryKey: ["source"] });
      onCancel();
    },
    onError: () => errorToast("Failed to update RSVP"),
  });

  const handleAddContactByHandle = (_contact: Contact, handle: Handle) => {
    if (participants.some((p) => p.email?.toLowerCase() === handle.value.toLowerCase())) return;
    setParticipants((prev) => [
      ...prev,
      { urn: handle.urn, email: handle.value, name: _contact.name, role: "requiredAttendee" },
    ]);
    setAddedHandleIds((prev) => [...prev, handle.id]);
  };

  const handleAddContact = (contact: Contact) => {
    const emailHandle = contact.handles?.find((h) => h.type === "emailAddress");
    if (!emailHandle) return;
    handleAddContactByHandle(contact, emailHandle);
  };

  const handleAddEmail = async (email: string) => {
    if (participants.some((p) => p.email?.toLowerCase() === email.toLowerCase())) return;
    const contact = await createContactWithHandle({
      handleType: "emailAddress",
      handleValue: email,
    });
    const handle = contact.handles?.[0];
    if (!handle) return;
    handleAddContactByHandle(contact, handle);
  };

  const handleRemoveParticipant = (participant: CalendarEventParticipant) => {
    const email = participant.email || participant.urn;
    setParticipants((prev) => prev.filter((p) => (p.email || p.urn) !== email));

    // Extract handle ID from URN (format: urn:bb:handle:HANDLE_ID)
    if (participant.urn) {
      const parts = participant.urn.split(":");
      const handleId = parts[parts.length - 1];
      if (handleId) {
        // Check if this was a newly added participant
        if (addedHandleIds.includes(handleId)) {
          setAddedHandleIds((prev) => prev.filter((id) => id !== handleId));
        } else {
          setRemovedHandleIds((prev) => [...prev, handleId]);
        }
      }
    }
  };

  const [showRsvpScopeDialog, setShowRsvpScopeDialog] = useState(false);
  const pendingRsvpRef = useRef<"accepted" | "tentative" | "declined" | undefined>(undefined);

  const handleRsvp = (
    rsvp: "accepted" | "tentative" | "declined",
    editScope?: RecurringEditScope,
  ) => {
    if (!event.externalEventId || !event.gcalId) return;
    // For a recurring meeting, ask whether the RSVP applies to just this
    // occurrence or the whole series before submitting (BIG-5288).
    if (event.isRecurring && !editScope) {
      pendingRsvpRef.current = rsvp;
      setShowRsvpScopeDialog(true);
      return;
    }
    rsvpMutation.mutate({
      sourceEntId: event.gcalId,
      calendarId: event.calendarId,
      externalEventId: event.externalEventId,
      rsvp,
      editScope,
    });
  };

  // The title-bar triggers, shared by this panel's own header and by a parent
  // that renders one instead (see onActionsChange).
  const startRename = useCallback(() => {
    renameOpenValueRef.current = form.getFieldValue("summary");
    setIsRenaming(true);
  }, [form]);
  const canDelete = !!event.externalEventId;
  // Relayed, not owned. The confirmation lives above this panel (see
  // `eventPendingDeleteAtom`) precisely so it is not a child of the thing being
  // deleted — the hosts remount this by key, which used to take the dialog with
  // it.
  //
  // A raw relay, deliberately unguarded. The host closes this panel on the way
  // to confirming, so an unsaved edit does have to be settled first — but the
  // host is what closes, so the host is what guards, and every one of them
  // routes this through `attemptClose` already. Guarding here as well prompts
  // twice wherever the panel draws its own header, since that path runs both.
  const requestDelete = useCallback(() => onRequestDelete?.(), [onRequestDelete]);

  useEffect(() => {
    if (!onActionsChange) return;
    onActionsChange({
      title: draftSummary,
      canRename: canEdit,
      canDelete,
      startRename,
      requestDelete,
    });
    // Retract on unmount so a parent's title bar can't outlive the panel that
    // services it — the card remounts this panel by key on an external edit.
    return () => onActionsChange(null);
  }, [onActionsChange, draftSummary, canEdit, canDelete, startRename, requestDelete]);

  const isPending = updateMutation.isPending || rsvpMutation.isPending;

  const sortedParticipants = useMemo(
    () =>
      [...participants].sort((a, b) =>
        a.role === "organizer" ? -1 : b.role === "organizer" ? 1 : 0,
      ),
    [participants],
  );

  // Header count + RSVP breakdown, rendered only when somebody is actually on
  // the event. The breakdown is omitted when it's just you (≤1 participant).
  const participantCount = sortedParticipants.length;
  const participantSummary = useMemo(() => {
    if (sortedParticipants.length <= 1) return null;
    const counts = { yes: 0, no: 0, maybe: 0, awaiting: 0 };
    for (const p of sortedParticipants) {
      if (p.state === "attending") counts.yes++;
      else if (p.state === "declined") counts.no++;
      else if (p.state === "tentative") counts.maybe++;
      else counts.awaiting++;
    }
    return [
      counts.yes && `${counts.yes} yes`,
      counts.no && `${counts.no} no`,
      counts.maybe && `${counts.maybe} maybe`,
      counts.awaiting && `${counts.awaiting} awaiting`,
    ]
      .filter(Boolean)
      .join(", ");
  }, [sortedParticipants]);

  /**
   * Leave the panel for another screen: settle any unsaved edits, dismiss the
   * surface if it is an overlay, then let the link proceed. "Keep editing"
   * resolves the prompt without ever calling back, which is what cancels the
   * navigation.
   */
  const [, setLocation] = useLocation();
  const dismissBeforeNavigating = useCallback(
    (proceed: () => void) =>
      attemptClose(() => {
        onNavigateAway?.();
        proceed();
      }),
    [attemptClose, onNavigateAway],
  );

  // Held in one place because three different rows can be the panel's first —
  // timed, all-day, and read-only — and only one of them renders at a time.

  // The event's own actions, for the bar this panel draws when no parent owns
  // the header. `never`-tier: a panel showing one event is narrow at every width
  // it appears at, so there is none at which spreading these across the row
  // would be right — the shared split still promotes a lone one back out.
  const menuTriggerRef = useRef<HTMLButtonElement>(null);
  // No width measured: this panel's actions are all `never`-tier, so the split
  // is the same at every width. See `splitPanelHeaderActions`.
  const { menuActions, soleAction, showMenu } = splitPanelHeaderActions({
    actions: [
      {
        key: "rename",
        icon: Pencil,
        label: "Rename",
        onClick: startRename,
        statsigName: "event-rename",
        tier: "never",
        visible: canEdit,
      },
      {
        key: "delete",
        icon: Trash2,
        label: "Delete event",
        onClick: requestDelete,
        statsigName: "event-delete",
        tier: "never",
        visible: canDelete,
        iconClassName: "text-destructive",
      },
    ],
  });

  // The title, in whichever bar is hosting it. Portalled into a parent's header
  // when `titleContainer` is set, so the same form-bound field serves both
  // positions — a rename opened from the card's header edits it in place there.

  // A read-only event's title is plain text: there is no draft to read it from,
  // so it never enters the form and skips the field subscription entirely.
  const titleNode = !canEdit ? (
    <EventTitleText>{event.summary}</EventTitleText>
  ) : (
    <form.Field name="summary">
      {(field) =>
        isRenaming ? (
          <EventTitleEditor
            textareaRef={renameTextareaRef}
            value={field.state.value}
            onChange={(next) => {
              field.handleChange(next);
              setSummaryDirty(next !== originalSummary);
            }}
            onCommit={() => setIsRenaming(false)}
            onCancel={cancelRename}
          />
        ) : (
          // The draft, not `event.summary` — an unsaved rename shows here.
          <EventTitleText onEdit={startRename}>{field.state.value}</EventTitleText>
        )
      }
    </form.Field>
  );

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        e.stopPropagation();
        form.handleSubmit();
      }}
      // Transparent flex column so SidebarContent's `flex-1 min-h-0 overflow-auto`
      // engages against the parent flex column. Without this, the form is
      // content-sized and the panel overflows the viewport with no scroll.
      //
      // `[&_button]:...ring-0` suppresses focus rings on every control in the
      // panel. Radix triggers (Select/Popover) restore focus to themselves on
      // close, which Chrome treats as `:focus-visible` even after a mouse click —
      // so the keyboard-only ring spuriously reappears when you open then close a
      // dropdown with the mouse. CSS can't distinguish that from real keyboard
      // focus, so we drop the ring panel-wide.
      className="flex h-full min-h-0 flex-col [&_button]:focus:ring-0 [&_button]:focus:ring-offset-0 [&_button]:focus-visible:ring-0 [&_button]:focus-visible:ring-offset-0"
    >
      {/* The title, and the panel's own bar around it. When `titleContainer` is
          set the bar is dropped and only the title is portalled into the
          parent's header — the field stays bound to this form either way, so the
          inline rename editor works from either position. */}
      {titleContainer ? createPortal(titleNode, titleContainer) : null}
      {/* `bg-transparent` rather than the shell's default page colour: this panel
          renders on whatever surface hosts it — white inside the event popover,
          the page's own ground on the event screen — and a header is the colour
          of what it heads. Nothing shows through it: the body is a sibling that
          clips at its own edge rather than scrolling under. */}
      {!titleContainer && (
        // `grows` only while renaming: the editor soft-wraps a long title to
        // three lines, and a fixed row neither clips it nor yields, so it would
        // paint over the first section of the body. At rest the row is back to
        // one height, level with every other panel header on screen.
        <SplitPanelHeader className="bg-transparent" grows={isRenaming}>
          {titleNode}
          {/* The event's own actions collect behind a menu, the way thread
              detail's do: they are about the event, while expand and close are
              about the panel showing it, and a row mixing the two reads as one
              undifferentiated strip. A lone action is promoted back out — a menu
              holding one thing costs a click and saves nothing. */}
          {soleAction && <PanelHeaderActionButton action={soleAction} hideBelowTier={false} />}
          <PanelHeaderMenu
            title={draftSummary}
            label="Open event actions menu"
            show={showMenu}
            triggerRef={menuTriggerRef}
            statsigName="event-detail-menu"
          >
            {menuActions.map((a) => (
              <PanelHeaderMenuItem key={a.key} action={a} />
            ))}
          </PanelHeaderMenu>
          {/* Before the dismiss, as `ArtifactBiscuit` orders its own: the two
              controls that are about where you are reading, rather than about
              the event, sit together. A bare arrow rather than a boxed one
              because the fuller surface is still inside the app, whether the
              host cards it onto the canvas or falls back to the event's page. */}
          {onExpand && (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  data-statsig-name="event-expand"
                  onClick={onExpand}
                  className={panelHeaderControlClass}
                  type="button"
                >
                  <ArrowUpRight />
                  <span className="sr-only">Expand</span>
                </Button>
              </TooltipTrigger>
              <TooltipContent>Expand</TooltipContent>
            </Tooltip>
          )}
          {!hideClose && (
            <Button
              variant="ghost"
              size="icon"
              data-statsig-name="sidebar-close"
              onClick={() => attemptClose(onClose)}
              className={cn(panelHeaderControlClass, panelTrailingIconClass)}
              type="button"
            >
              <X />
              <span className="sr-only">Close</span>
            </Button>
          )}
        </SplitPanelHeader>
      )}

      {/* Recurring event scope dialogs */}
      <RecurringEditScopeDialog
        open={showEditScopeDialog}
        onClose={() => {
          setShowEditScopeDialog(false);
          // If "Save" from the unsaved-changes prompt opened this scope dialog
          // and the user dismissed it without choosing, drop the deferred close
          // so it can't fire unexpectedly on a later, unrelated save.
          closeAfterSaveRef.current = null;
        }}
        onConfirm={(scope) => {
          setShowEditScopeDialog(false);
          pendingEditScopeRef.current = scope;
          form.handleSubmit();
        }}
        mode="edit"
        isPending={updateMutation.isPending}
      />
      <RecurringEditScopeDialog
        open={showRsvpScopeDialog}
        onClose={() => setShowRsvpScopeDialog(false)}
        onConfirm={(scope) => {
          setShowRsvpScopeDialog(false);
          const rsvp = pendingRsvpRef.current;
          if (rsvp) handleRsvp(rsvp, scope);
        }}
        mode="rsvp"
        isPending={rsvpMutation.isPending}
      />

      {/* Unsaved-changes prompt — shown when a dismiss is attempted while dirty. */}
      <Dialog open={!!pendingClose} onOpenChange={(o) => !o && setPendingClose(null)}>
        <DialogContent className="max-w-sm">
          <div className="space-y-4">
            <h3 className="text-lg font-semibold">Unsaved changes</h3>
            <p className="text-sm text-muted-foreground">
              You have unsaved changes to this event. Save them before closing?
            </p>
            <div className="flex items-center justify-end gap-2">
              <Button
                variant="ghost"
                data-statsig-name="event-unsaved-keep-editing"
                onClick={() => setPendingClose(null)}
                type="button"
              >
                Keep editing
              </Button>
              <Button
                variant="outline"
                data-statsig-name="event-unsaved-discard"
                onClick={() => {
                  const proceed = pendingClose;
                  setPendingClose(null);
                  proceed?.();
                }}
                type="button"
                disabled={isPending}
              >
                Discard
              </Button>
              <Button
                data-statsig-name="event-unsaved-save"
                onClick={() => {
                  // Defer the close until the save lands (see updateMutation.onSuccess).
                  closeAfterSaveRef.current = pendingClose;
                  setPendingClose(null);
                  form.handleSubmit();
                }}
                type="button"
                disabled={isPending}
              >
                Save
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* `[&>*]:shrink-0`: this is a flex-col scroll container, and flex children
          default to shrink:1 — so when content (e.g. a long auto-grown description)
          exceeds the panel height, flexbox compresses each section row instead of
          scrolling, and fixed-height children spill over the rows below. Pinning
          children to their natural height makes the panel overflow-scroll instead. */}
      <SidebarContent className="p-4 gap-4 [&_button]:font-normal [&>*]:shrink-0">
        {/* Date & Time */}
        {canEdit ? (
          <>
            {/* Date */}
            <div className="flex items-center gap-3">
              <SectionIcon icon={CalendarDays} label="Date" />
              {isAllDay ? (
                <div className="flex items-center gap-1.5 flex-wrap">
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        type="button"
                        variant="outline"
                        size="xs"
                        data-statsig-name="event-edit-allday-start-date"
                      >
                        {format(startDate, "MMM d, yyyy")}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <DatePickerCalendar selected={startDate} onSelect={handleStartDateChange} />
                    </PopoverContent>
                  </Popover>
                  <span className="text-sm text-muted-foreground">–</span>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        type="button"
                        variant="outline"
                        size="xs"
                        data-statsig-name="event-edit-allday-end-date"
                      >
                        {format(endDate, "MMM d, yyyy")}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <DatePickerCalendar selected={endDate} onSelect={handleEndDateChange} />
                    </PopoverContent>
                  </Popover>
                </div>
              ) : (
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      type="button"
                      variant="outline"
                      size="xs"
                      data-statsig-name="event-edit-date"
                    >
                      {format(startDate, "MMMM d, yyyy")}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <DatePickerCalendar selected={startDate} onSelect={handleStartDateChange} />
                  </PopoverContent>
                </Popover>
              )}
            </div>

            {/* All-day toggle — mini switch in the leading icon column */}
            <div className="flex items-center gap-3" data-statsig-name="event-edit-all-day">
              <Toggle
                id="event-edit-all-day"
                size="sm"
                checked={isAllDay}
                onCheckedChange={handleAllDayChange}
                aria-label="All day"
              />
              <label
                htmlFor="event-edit-all-day"
                className="cursor-pointer text-sm text-muted-foreground"
              >
                All day
              </label>
            </div>

            {/* Free/busy — whether the event occupies the user's time */}
            <div className="flex items-center gap-3" data-statsig-name="event-edit-availability">
              <SectionIcon icon={CircleDot} label="Shows as" />
              <Select
                value={availability}
                onValueChange={(value) => setAvailability(value as Availability)}
              >
                <SelectTrigger
                  variant="editable"
                  className="h-7 px-2 text-sm w-auto"
                  aria-label="Shows as"
                >
                  {availability === "free" ? "Free" : "Busy"}
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="busy">Busy</SelectItem>
                  <SelectItem value="free">Free</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Time (timed events only) */}
            {!isAllDay && (
              <div className="flex items-center gap-3">
                <SectionIcon icon={Clock} label="Time" />
                <div className="flex items-center gap-1.5">
                  <Select value={startTime} onValueChange={handleStartTimeChange}>
                    {/* Pill shows the compact time (no ":00", px-2 like the other
                        pills); the dropdown below keeps the full "9:00 AM" form. */}
                    <SelectTrigger
                      variant="editable"
                      className="h-7 px-2 text-sm w-auto"
                      aria-label="Start time"
                    >
                      {formatTimeLabelCompact(startTime)}
                    </SelectTrigger>
                    <SelectContent>
                      {startTimeOptions.map((opt) => (
                        <SelectItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <span className="text-sm text-muted-foreground">–</span>
                  <Select value={endTime} onValueChange={handleEndTimeChange}>
                    {/* Compact time only in the pill — the duration hint stays in
                        the dropdown options, not the collapsed trigger. */}
                    <SelectTrigger
                      variant="editable"
                      className="h-7 px-2 text-sm w-auto"
                      aria-label="End time"
                    >
                      {formatTimeLabelCompact(endTime)}
                    </SelectTrigger>
                    <SelectContent>
                      {endTimeOpts.map((opt) => (
                        <SelectItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            )}
          </>
        ) : (
          <div className="flex items-center gap-3">
            <SectionIcon icon={CalendarDays} label="Date & time" />
            <p className="text-sm text-muted-foreground">{event.formattedRange}</p>
          </div>
        )}

        {/* Recurrence */}
        {canEdit ? (
          <RecurrencePicker
            value={recurrence}
            onChange={setRecurrence}
            eventDate={startDate}
            triggerClassName={pillSelectTriggerClass}
            triggerVariant="editable"
          />
        ) : event.isRecurring && event.recurrence?.[0] ? (
          <div className="flex items-center gap-3">
            <SectionIcon icon={Repeat} label="Recurrence" />
            <span className="text-sm text-muted-foreground">
              {rruleToText(event.recurrence[0], startDate)}
            </span>
          </div>
        ) : null}

        {/* The brief Okthx wrote, above the link that joins the meeting: prep
            reads before the call it prepares for. Renders its own row, or
            nothing at all when the meeting was never briefed. */}
        <EventBriefSection eventId={event.gcalId} dismissPanel={dismissBeforeNavigating} />

        {/* What okthx last did to the event. Its own row, or nothing for an
            event okthx never touched. */}
        <EventOkthxActionLine eventId={event.gcalId} />

        {/* Meeting link — omitted entirely when read-only with no meeting, so it
            doesn't leave an empty flex item that doubles the section gap. */}
        {(hasMeeting || canEdit) && (
          <div className="flex items-center gap-3">
            {hasMeeting && meetLink ? (
              <>
                <SectionIcon icon={Video} label="Video conferencing" />
                <Button asChild variant="primary" size="xs" data-statsig-name="event-join-meeting">
                  <a href={meetLink} target="_blank" rel="noopener noreferrer">
                    {getMeetLinkLabel(meetLink)}
                  </a>
                </Button>
                {canEdit && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    data-statsig-name="event-remove-meeting"
                    onClick={() => setHasMeeting(false)}
                    className="size-6 shrink-0 text-muted-foreground ml-auto"
                  >
                    <X className="size-3.5" />
                  </Button>
                )}
              </>
            ) : hasMeeting && !meetLink ? (
              <>
                <SectionIcon icon={Video} label="Video conferencing" />
                <span className="text-sm text-muted-foreground flex-1 flex items-center gap-2">
                  {isPendingMeeting ? (
                    <>
                      {/* `meetingUrl === "pending"` means the optimistic write
                          landed and the provider is creating the meeting right
                          now, so show an active spinner rather than the static
                          "will be created" copy used for an unsaved add. Kept
                          provider-neutral: `addVirtualMeeting` can create a
                          Google Meet or a Teams meeting depending on calendar. */}
                      <Loader2 className="size-3.5 animate-spin shrink-0" />
                      Creating video meeting…
                    </>
                  ) : (
                    "Video Meeting (will be created)"
                  )}
                </span>
                {canEdit && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    data-statsig-name="event-remove-meeting"
                    onClick={() => setHasMeeting(false)}
                    className="size-6 shrink-0 text-muted-foreground"
                  >
                    <X className="size-3.5" />
                  </Button>
                )}
              </>
            ) : canEdit ? (
              <>
                <SectionIcon icon={Video} label="Video conferencing" />
                <Button
                  type="button"
                  variant="outline"
                  size="xs"
                  data-statsig-name="event-add-meeting"
                  onClick={() => setHasMeeting(true)}
                >
                  Add video call
                </Button>
              </>
            ) : null}
          </div>
        )}

        {/* Location — the text is a maps link (like the meet link above), so
            editing gets its own pencil rather than a click on the text. The
            empty state still opens the picker directly: nothing to link to. */}
        {canEdit ? (
          <div className="flex min-h-8 items-center gap-3">
            <SectionIcon icon={MapPin} label="Location" />
            <form.Field name="location">
              {(field) =>
                locationOpen ? (
                  <InlineLocationInput
                    autoFocus
                    value={field.state.value}
                    onChange={(next) => {
                      field.handleChange(next);
                      setLocationDirty(next !== initialPhysicalLocation);
                    }}
                    onBlur={() => {
                      field.handleBlur();
                      setLocationOpen(false);
                    }}
                    placeholder="Add a location"
                    inputClassName="h-8 rounded-md py-1 text-sm text-foreground/80 focus-visible:ring-0 focus-visible:ring-offset-0"
                  />
                ) : field.state.value ? (
                  <>
                    <LocationDisplay
                      location={field.state.value}
                      className="link truncate text-sm"
                    />
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          data-statsig-name="event-edit-location"
                          onClick={() => setLocationOpen(true)}
                          className="size-6 shrink-0"
                        >
                          <Pencil className="size-3.5" />
                          <span className="sr-only">Edit location</span>
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>Edit location</TooltipContent>
                    </Tooltip>
                  </>
                ) : (
                  <Button
                    type="button"
                    variant="outline"
                    size="xs"
                    data-statsig-name="event-add-location"
                    onClick={() => setLocationOpen(true)}
                  >
                    Add a location
                  </Button>
                )
              }
            </form.Field>
          </div>
        ) : initialPhysicalLocation ? (
          <div className="flex items-center gap-3">
            <SectionIcon icon={MapPin} label="Location" />
            <LocationDisplay location={initialPhysicalLocation} className="link truncate text-sm" />
          </div>
        ) : null}

        <Separator orientation="horizontal" className="w-full" />

        {/* My RSVP status */}
        {showRsvp && (
          <div className="space-y-1.5">
            <div className="flex items-center gap-3">
              <SectionIcon icon={CalendarCheck} label="My RSVP status" />
              <Label>My RSVP status</Label>
            </div>
            <div className="mt-2 flex items-center gap-1.5">
              {RSVP_OPTIONS.map((opt) => (
                <Button
                  key={opt.value}
                  type="button"
                  // Selected reads as a chosen segment (filled neutral + check),
                  // not a CTA; unselected stays a plain outline.
                  variant={currentRsvp === opt.value ? "secondary" : "outline"}
                  size="sm"
                  data-statsig-name={`event-rsvp-${opt.value}`}
                  disabled={rsvpMutation.isPending}
                  className={cn(
                    "flex-1 h-7 gap-1 text-xs px-2",
                    currentRsvp === opt.value && "pointer-events-none",
                  )}
                  onClick={() => handleRsvp(opt.value)}
                >
                  {rsvpMutation.isPending && rsvpMutation.variables?.rsvp === opt.value ? (
                    <Loader2 className="size-3 animate-spin" />
                  ) : (
                    <>
                      {currentRsvp === opt.value && <Check className="size-3 shrink-0" />}
                      {opt.label}
                    </>
                  )}
                </Button>
              ))}
            </div>
          </div>
        )}
        {showRsvp && <Separator orientation="horizontal" className="w-full" />}

        {/* Participants. An event with nobody on it — a solo hold, or anything
            booked onto a shared calendar — collapses to the same "Add a
            location" affordance the rows above use, and disappears entirely
            when there is no way to add anyone. */}
        {sortedParticipants.length === 0 ? (
          canEdit ? (
            <div className="flex min-h-8 items-center gap-3">
              <SectionIcon icon={User} label="Participants" />
              <ContactInput
                onSelectContact={handleAddContact}
                onSelectHandle={handleAddContactByHandle}
                onSelectEmail={handleAddEmail}
                filterHandleTypes={["emailAddress"]}
                placeholder="Add participant..."
                buttonLabel="Add participant"
                buttonClassName="hover:bg-accent hover:text-accent-foreground hover:border-accent"
                statsigName="event-edit-add-participant"
              />
            </div>
          ) : null
        ) : (
          <div className="space-y-1.5">
            {/* Summary heading: count + RSVP breakdown */}
            <div className="flex items-center gap-3">
              <SectionIcon icon={User} label="Participants" />
              <div className="flex flex-col">
                <span className="text-sm font-medium">
                  {participantCount} participant{participantCount === 1 ? "" : "s"}
                </span>
                {participantSummary && (
                  <span className="text-xs text-muted-foreground">{participantSummary}</span>
                )}
              </div>
            </div>
            <div className="space-y-1">
              {sortedParticipants.map((p) => {
                const participantIsOrganizer = p.role === "organizer";
                const email = p.email || p.urn;
                const displayName = p.name || email;
                const contactHref = p.contactId ? `/contacts/${p.contactId}` : null;
                return (
                  <div key={p.email || p.urn} className="flex items-center gap-2 text-sm">
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <div className="relative shrink-0">
                          <AvatarWithFallback
                            size="sm"
                            className="border"
                            person={{ name: displayName, value: email }}
                          />
                          <ParticipantStatusBadge state={p.state} />
                        </div>
                      </TooltipTrigger>
                      <TooltipContent side="left">
                        {participantStatusTooltip(displayName, p.state)}
                      </TooltipContent>
                    </Tooltip>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        {contactHref ? (
                          // A real link rather than a click handler: the
                          // surrounding nav boundary decides whether the contact
                          // drills onto the canvas, opens the sidebar, or loads
                          // the page, and cmd/middle-click keep working. A plain
                          // click still leaves the panel, so it settles unsaved
                          // edits and dismisses an overlay host first, the way the
                          // brief link does.
                          <Link
                            href={contactHref}
                            aria-label={`Open ${displayName}`}
                            className="flex-1 truncate min-w-0 rounded-sm hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                            onClick={(e) => {
                              if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
                              e.preventDefault();
                              dismissBeforeNavigating(() => setLocation(contactHref));
                            }}
                          >
                            {displayName}
                          </Link>
                        ) : (
                          <span className="flex-1 truncate min-w-0 cursor-default">
                            {displayName}
                          </span>
                        )}
                      </TooltipTrigger>
                      <TooltipContent side="top">{email}</TooltipContent>
                    </Tooltip>
                    {event.gcalId && p.email && (
                      <ParticipantInviteAction eventId={event.gcalId} email={p.email} />
                    )}
                    {participantIsOrganizer ? (
                      <span className="text-xs text-muted-foreground shrink-0">Organizer</span>
                    ) : canEdit ? (
                      <Button
                        variant="ghost"
                        size="icon"
                        data-statsig-name="event-edit-remove-participant"
                        onClick={() => handleRemoveParticipant(p)}
                        className="size-6 shrink-0"
                      >
                        <X className="size-3.5" />
                      </Button>
                    ) : null}
                  </div>
                );
              })}
            </div>
            {canEdit && (
              <ContactInput
                onSelectContact={handleAddContact}
                onSelectHandle={handleAddContactByHandle}
                onSelectEmail={handleAddEmail}
                filterHandleTypes={["emailAddress"]}
                placeholder="Add participant..."
                buttonLabel="+ Add"
                buttonClassName="hover:bg-accent hover:text-accent-foreground hover:border-accent"
                statsigName="event-edit-add-participant"
              />
            )}
          </div>
        )}

        <Separator orientation="horizontal" className="w-full" />

        {/* Description — renders the saved HTML (links, emphasis) when viewing;
            click it to edit the raw source, which persists on the panel's Save. */}
        {canEdit ? (
          <div className="flex min-h-8 items-start gap-3">
            <SectionIcon icon={SquarePen} label="Description" className="mt-1.5" />
            {descriptionOpen ? (
              <Textarea
                id="event-description"
                ref={descriptionTextareaRef}
                rows={1}
                value={description}
                placeholder="Add description"
                onChange={(e) => {
                  const next = e.target.value;
                  setDescription(next);
                  setDescriptionDirty(next !== (event.description ?? ""));
                }}
                onBlur={() => setDescriptionOpen(false)}
                className={cn(
                  "flex-1 resize-none min-h-8 py-1 focus-visible:ring-0 focus-visible:ring-offset-0",
                  autoGrowTextareaClass,
                )}
              />
            ) : description.trim() ? (
              // biome-ignore lint/a11y/useSemanticElements: a <button> would nest the rendered description's <a> links (invalid interactive nesting); keyboard access is via tabIndex + onKeyDown.
              <div
                role="button"
                tabIndex={0}
                className="flex-1 cursor-text rounded-md py-1"
                onClick={(e) => {
                  // Clicking a link follows it; clicking elsewhere edits the source.
                  if ((e.target as HTMLElement).closest("a")) return;
                  enterDescriptionEdit();
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    enterDescriptionEdit();
                  }
                }}
              >
                <DescriptionHtml html={description} />
              </div>
            ) : (
              <Button
                type="button"
                variant="outline"
                size="xs"
                data-statsig-name="event-add-description"
                onClick={enterDescriptionEdit}
              >
                Add description
              </Button>
            )}
          </div>
        ) : event.description ? (
          <div className="flex items-start gap-3">
            <SectionIcon icon={SquarePen} label="Description" className="mt-1.5" />
            <DescriptionHtml html={event.description} className="flex-1" />
          </div>
        ) : null}

        {/* Reminders */}
        <RemindersSection
          value={reminders}
          onChange={handleRemindersChange}
          disabled={!canEditReminders}
          loading={remindersLoading}
          triggerVariant="editable"
        />

        {/* Calendar account (color "icon" + clickable name) + open link */}
        <div className="flex items-center justify-between gap-2">
          {canEdit ? (
            <EventColorPicker
              calendarId={event.calendarId}
              colorId={colorId}
              onChange={setColorId}
              calendarName={event.calendarName}
            />
          ) : (
            <div className="flex min-w-0 items-center gap-2">
              <ColorIndicator calendarId={event.calendarId} colorId={colorId} />
              <p className="text-sm text-muted-foreground truncate min-w-0">{event.calendarName}</p>
            </div>
          )}
          {event.htmlLink && (
            <a
              href={event.htmlLink}
              target="_blank"
              rel="noopener noreferrer"
              className="link flex items-center gap-1 text-sm shrink-0"
            >
              <ExternalLink className="size-3.5" />
              Open
            </a>
          )}
        </div>
      </SidebarContent>

      {/* Save / Cancel — pinned footer (outside the scroll area) so edits can be
          applied without scrolling, and nothing shows through beneath it. Shown
          when dirty; a participant on a read-only event can still save a
          reminder-only edit (their personal copy). */}
      {isDirty && (canEdit || (remindersDirty && canEditReminders)) && (
        <div className="shrink-0 border-t border-border px-4 py-3">
          {timeRangeInvalid && (
            <p role="alert" className="mb-2 text-sm text-destructive">
              This event ends before it starts — adjust the end time.
            </p>
          )}
          <div className="flex items-center gap-2">
            <Button
              type="button"
              variant="outline"
              data-statsig-name="event-edit-cancel"
              onClick={onCancel}
              className="flex-1"
              disabled={isPending}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              data-statsig-name="event-edit-save"
              disabled={isPending || timeRangeInvalid}
              className="flex-1"
            >
              {updateMutation.isPending ? <Loader2 className="size-4 animate-spin mr-2" /> : null}
              Save
            </Button>
          </div>
        </div>
      )}
    </form>
  );
};
